import React, { useState } from 'react';
import { Minus, Plus, ShoppingCart } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { addToCart } from '../store/slices/cartSlice';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';


const hotBeveragesItems = [
  {
    id: 'flavouredTea',
    name: "Flavoured Tea",
    description: "A delightful blend of tea with a hint of natural flavors to warm you up.",
    price: 1500,
    isAvailable: true,
    imageUrl: 'https://10eleven.org/wp-content/uploads/2022/08/Flavor-Tea.jpg' // Image of flavoured tea
  },
  {
    id: 'tea',
    name: "Tea",
    description: "Classic brewed tea served hot, perfect for any time of the day.",
    price: 1000,
    isAvailable: true,
    imageUrl: 'https://images.unsplash.com/photo-1511920170033-f8396924c348' // Image of classic tea
  },
  {
    id: 'coffee',
    name: "Coffee",
    description: "Rich and aromatic coffee brewed to perfection to energize your morning.",
    price: 1500,
    isAvailable: true,
    imageUrl: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085' // Image of coffee
  }
];



const HotBeveragesComponent = () => {
  const dispatch = useDispatch();
  const [selections, setSelections] = useState({});

  /**
   * Adjusts the quantity for a given beverage.
   * @param {string} itemId - Unique identifier for the beverage.
   * @param {number} delta - Change in quantity (+1 or -1).
   */
  const handleQuantityChange = (itemId, delta) => {
    setSelections(prev => ({
      ...prev,
      [itemId]: Math.max(1, (prev[itemId] || 1) + delta)
    }));
  };

  /**
   * Adds the selected beverage to the cart.
   * @param {object} item - The beverage item.
   */
  const handleAddToCart = (item) => {
    const quantity = selections[item.id] || 1;
    const cartItem = {
      id: `${item.id}-${Date.now()}`, // Unique cart item ID
      name: item.name,
      price: item.price,
      quantity
    };
    dispatch(addToCart(cartItem));
    toast.success(`${item.name} added to cart!`, {
      position: "top-right",
      autoClose: 3000
    });
  };

  /**
   * Renders a single hot beverage card.
   * @param {object} item - The beverage item.
   */
  const renderMenuItem = (item) => {
    const quantity = selections[item.id] || 1;
    return (
      <div key={item.id} className="bg-black rounded-xl overflow-hidden shadow-lg border border-gray-800 relative">
        <div className="relative">
          <img
            src={item.imageUrl}
            alt={item.name}
            className="w-full h-48 object-cover"
          />
          {/* Overlay for unavailable items */}
          {!item.isAvailable && (
            <div className="absolute inset-0 bg-black bg-opacity-75 flex justify-center items-center">
              <span className="text-red-600 font-bold text-xl">Not Available</span>
            </div>
          )}
          <div className="absolute top-4 right-4">
            <div className="bg-black bg-opacity-50 px-3 py-1 rounded-full">
              <span className="bg-red-600 text-white font-medium text-lg py-1 px-3 rounded-full">
                ₦{item.price.toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <h3 className="text-red-500 font-bold text-xl">{item.name}</h3>
            <p className="text-gray-400 mt-1 text-sm">{item.description}</p>
          </div>

          {/* Render interactive controls only if the item is available */}
          {item.isAvailable && (
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center gap-2 bg-black rounded-lg p-1">
                <button
                  onClick={() => handleQuantityChange(item.id, -1)}
                  className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                  disabled={quantity <= 1}
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="w-8 text-center font-medium text-red-500">
                  {quantity}
                </span>
                <button
                  onClick={() => handleQuantityChange(item.id, 1)}
                  className="p-1 text-gray-300 hover:text-red-500 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              <button
                onClick={() => handleAddToCart(item)}
                className="flex items-center gap-2 px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
                disabled={!item.isAvailable}
              >
                <ShoppingCart className="w-4 h-4" />
                <span>{item.isAvailable ? 'Add to Cart' : 'Not Available'}</span>
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-black text-red-500">
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Grid layout for hot beverage cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {hotBeveragesItems.map(item => renderMenuItem(item))}
        </div>
      </div>
    </div>
  );
};

export default HotBeveragesComponent;
